import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { FoldToggle } from '@/components/admin/sessions/walk/walk-fold';
import type { FoldState } from '@/components/admin/sessions/walk/walk-fold';
import type { ReactNode } from 'react';

/**
 * The two pieces the blocks of the page protocole share (refonte-modulaire.md §12.2, « Depuis
 * #224 » ; maquette `Vue de session`): the HEADER and the FOOT of « Rangement de la matière » and
 * « Détermination du noyau ».
 *
 * ## One header for both blocks
 *
 * The small label « Bloc de la page 5 » (the rank of the protocol page they hang on), the title
 * as an `h3` of 16 px, the pill of the state, then - folded - the summary and the facts on the
 * right (model, time, cost) and the fold button. The two blocks used to say it differently, one
 * with a small « Bloc » and the other with a large title: they are two blocks of one page and
 * they now read as such. What differs is only what each puts in the slots.
 *
 * ## The foot says where the block came from, and carries its dry trial
 *
 * « Rangée en fond, à la clôture du protocole » on the left; on the right, the tool of the dry
 * trial of the block - the selector « Source » and « Relancer l'analyste à blanc », « Relancer le
 * rangeur à blanc » - or, on the vitrine, the note that sends the operator to form.dev. The route
 * hands the trial over as a SLOT (`BlockTrialSlot`): the trials read and write through the query
 * client, which `SessionWalk` alone does not need (`sessions-parcours.test.tsx` mounts it without
 * one), so the walk draws a plain foot - the sentence of origin - and calls the slot, if there is
 * one, for the rest.
 */

/** What the walk tells a trial about the block it stands in: the sentence of origin its foot carries. */
export interface BlockFootInfo {
  readonly origin: string;
}

/** The slot a block calls for its foot (and what stands under it): the route's trial, or the vitrine's note. */
export type BlockTrialSlot = (foot: BlockFootInfo) => ReactNode;

export interface BlockFootProps {
  /** The sentence of origin. */
  readonly origin: string;
  /** What the foot says it carries - the mark `data-trial`. */
  readonly carries?: 'tools' | 'note' | 'none';
  /** The tool of the dry trial, or the vitrine's note, drawn on the right. */
  readonly children?: ReactNode;
}

export function BlockFoot({ origin, carries = 'none', children }: BlockFootProps) {
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.walkBlockFoot}
      data-trial={carries}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-gray-100 border-t bg-gray-50 px-4 py-2"
    >
      <span data-testid={SESSIONS_TEST_IDS.walkBlockOrigin} className="text-gray-600 text-caption">
        {origin}
      </span>
      {children === undefined || children === null ? null : (
        <div className="ml-auto flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

export interface BlockHeadProps {
  /** The rank of the protocol page (1-based), null when the block hangs on no visit. */
  readonly rank: number | null;
  readonly title: string;
  readonly fold: FoldState;
  /** What the toggle says while folded. */
  readonly closedLabel: string;
  readonly toggleTestId: string;
  /** The pills after the title: the state, and what asks for attention (`style absent`). */
  readonly pills: ReactNode;
  /** The line a FOLDED block says - the outcome and the facts - already wrapped in its mark; null once open. */
  readonly summary: ReactNode;
}

/** « Bloc de la page 5 » - or « Bloc du protocole » on a block that hangs on no visit. */
export function blockLabel(rank: number | null): string {
  return rank === null ? 'Bloc du protocole' : `Bloc de la page ${rank}`;
}

export function BlockHead({
  rank,
  title,
  fold,
  closedLabel,
  toggleTestId,
  pills,
  summary,
}: BlockHeadProps) {
  return (
    <header className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <span
        data-testid={SESSIONS_TEST_IDS.walkBlockLabel}
        className="font-semibold text-gray-600 text-caption uppercase tracking-wide"
      >
        {blockLabel(rank)}
      </span>
      <h3 className="font-semibold text-body text-gray-900">{title}</h3>
      <span className="inline-flex flex-wrap items-center gap-1.5">{pills}</span>
      {summary}
      <span className={fold.open ? 'ml-auto' : undefined}>
        <FoldToggle fold={fold} closedLabel={closedLabel} testId={toggleTestId} />
      </span>
    </header>
  );
}

export interface BlockSummaryProps {
  readonly testId: string;
  /** The outcome: the notes, or what the block came to. */
  readonly outcome: ReactNode;
  /** The facts on the right: the model, the time, the cost. */
  readonly facts: ReactNode;
}

/**
 * The line of a folded block, in the header: the outcome beside the title, the facts pushed to the
 * right. The wrapper is `display: contents`, so both take part in the header's own wrap while the
 * mark still holds the whole sentence.
 */
export function BlockSummary({ testId, outcome, facts }: BlockSummaryProps) {
  return (
    <span data-testid={testId} className="contents">
      {outcome}
      <span className="ml-auto inline-flex flex-wrap items-baseline gap-x-4 gap-y-1 text-gray-600 text-caption">
        {facts}
      </span>
    </span>
  );
}
